import * as Yup from 'yup';
import {validateEmail} from '../../../../utils/index.js';
import {caminhosComErro} from '../inscricao/inscricaoAdministrativaSchema.js';
import {
    FORMATO_DATA_HORA_WIRE,
    FORMATO_DATA_WIRE,
    MENSAGEM_COLEGIO_UM_PROCESSO,
    dataAteh,
    dataWireValida,
    regrasDoPrograma
} from './processoSelecao.js';

/*
 * Validação do formulário de Processo de Seleção. Cada regra diz o campo do
 * erro e a aba dele: o Salvar marca as abas com erro e abre a primeira. As
 * regras dependem do programa escolhido (campo idPrograma do próprio form).
 */

/* Abas da tela, na ordem do _form.xhtml. */
export const ABAS = Object.freeze([
    {id: 'geral', label: 'Geral'},
    {id: 'inscricao', label: 'Inscrição'},
    {id: 'agendamento', label: 'Agendamento'},
    {id: 'calendarios', label: 'Calendários'},
    {id: 'documentos', label: 'Documentos'},
    {id: 'email', label: 'Email'},
    {id: 'formulario', label: 'Formulário'},
    {id: 'ficha', label: 'Ficha'},
    {id: 'carga', label: 'Carga'}
]);

const OBRIGATORIO = 'É obrigatório';
const OBRIGATORIA = 'É obrigatória';
const DATA_INVALIDA = 'Data inválida';

const vazio = valor => valor === null || valor === undefined || String(valor).trim() === '';
const dataInvalida = (valor, formato) => !vazio(valor) && !dataWireValida(valor, formato);
const inscricao = v => regrasDoPrograma(v.idPrograma).exibirInscricao;
const processosVestibular = v => regrasDoPrograma(v.idPrograma).exibirInscricao && regrasDoPrograma(v.idPrograma).exibirProcessosVestibular;
// Limite da coluna (CBPROCESSOSELETIVO.DESCRICAO) e mensagem de intervalo, iguais às regras do back.
export const TAMANHO_MAXIMO_DESCRICAO = 250;
export const DATA_FINAL_ANTES_DO_INICIO = 'Deve ser igual ou posterior à data de início';

const quantidade = valor => (Array.isArray(valor) ? valor.length : 0);
const inteiroForaDaFaixa = (valor, minimo, maximo) => {
    if (vazio(valor)) return false;
    const numero = Number(String(valor).trim());
    return !Number.isInteger(numero) || numero < minimo || (maximo !== undefined && numero > maximo);
};

/* Cada regra: campo do erro, aba, mensagem e quando o valor é inválido. Por campo, a primeira que falhar vale. */
export const REGRAS = Object.freeze([
    {caminho: 'idPrograma', aba: 'geral', mensagem: OBRIGATORIO, invalido: v => vazio(v.idPrograma)},
    {caminho: 'descricao', aba: 'geral', mensagem: OBRIGATORIA, invalido: v => vazio(v.descricao)},
    {caminho: 'descricao', aba: 'geral', mensagem: `Máximo de ${TAMANHO_MAXIMO_DESCRICAO} caracteres`, invalido: v => String(v.descricao || '').trim().length > TAMANHO_MAXIMO_DESCRICAO},
    {caminho: 'numeroEdital', aba: 'geral', mensagem: 'Informe um número inteiro maior ou igual a 0', invalido: v => inteiroForaDaFaixa(v.numeroEdital, 0)},
    {caminho: 'anoVigente', aba: 'geral', mensagem: 'Informe um ano entre 2000 e 2100', invalido: v => inteiroForaDaFaixa(v.anoVigente, 2000, 2100)},
    {caminho: 'semestreReferencia', aba: 'geral', mensagem: OBRIGATORIO, invalido: v => vazio(v.semestreReferencia)},
    {caminho: 'nomeDoPrograma', aba: 'geral', mensagem: OBRIGATORIO, invalido: v => vazio(v.nomeDoPrograma)},
    {caminho: 'dataEdital', aba: 'geral', mensagem: OBRIGATORIA, invalido: v => vazio(v.dataEdital)},
    {caminho: 'dataEdital', aba: 'geral', mensagem: DATA_INVALIDA, invalido: v => dataInvalida(v.dataEdital, FORMATO_DATA_WIRE)},

    {caminho: 'dataInicial', aba: 'inscricao', mensagem: OBRIGATORIA, invalido: v => inscricao(v) && vazio(v.dataInicial)},
    {caminho: 'dataInicial', aba: 'inscricao', mensagem: DATA_INVALIDA, invalido: v => inscricao(v) && dataInvalida(v.dataInicial, FORMATO_DATA_HORA_WIRE)},
    {caminho: 'dataFinal', aba: 'inscricao', mensagem: OBRIGATORIA, invalido: v => inscricao(v) && vazio(v.dataFinal)},
    {caminho: 'dataFinal', aba: 'inscricao', mensagem: DATA_INVALIDA, invalido: v => inscricao(v) && dataInvalida(v.dataFinal, FORMATO_DATA_HORA_WIRE)},
    {
        caminho: 'dataFinal', aba: 'inscricao', mensagem: DATA_FINAL_ANTES_DO_INICIO,
        invalido: v => inscricao(v) && !dataAteh(v.dataInicial, v.dataFinal, FORMATO_DATA_HORA_WIRE)
    },
    {caminho: 'dataFinalAtualizacaoResultado', aba: 'inscricao', mensagem: OBRIGATORIA, invalido: v => inscricao(v) && vazio(v.dataFinalAtualizacaoResultado)},
    {
        caminho: 'dataFinalAtualizacaoResultado', aba: 'inscricao', mensagem: DATA_INVALIDA,
        invalido: v => inscricao(v) && dataInvalida(v.dataFinalAtualizacaoResultado, FORMATO_DATA_WIRE)
    },
    {caminho: 'termoCiencia', aba: 'inscricao', mensagem: OBRIGATORIO, invalido: v => inscricao(v) && vazio(v.termoCiencia)},
    {
        caminho: 'processosVestibular', aba: 'inscricao', mensagem: 'Informe ao menos um processo seletivo permitido',
        invalido: v => processosVestibular(v) && quantidade(v.processosVestibular) === 0
    },
    {
        caminho: 'processosVestibular', aba: 'inscricao', mensagem: MENSAGEM_COLEGIO_UM_PROCESSO,
        invalido: v => processosVestibular(v) && regrasDoPrograma(v.idPrograma).processoVestibularUnico && quantidade(v.processosVestibular) > 1
    },
    {caminho: 'vagas', aba: 'inscricao', mensagem: 'Informe ao menos um curso permitido', invalido: v => inscricao(v) && quantidade(v.vagas) === 0},
    {caminho: 'flagRevalidacao', aba: 'inscricao', mensagem: OBRIGATORIO, invalido: v => vazio(v.flagRevalidacao)},
    {caminho: 'flagSomenteInscricaoComCargas', aba: 'inscricao', mensagem: OBRIGATORIO, invalido: v => vazio(v.flagSomenteInscricaoComCargas)},
    {caminho: 'flagProtocoloEntrega', aba: 'inscricao', mensagem: OBRIGATORIO, invalido: v => vazio(v.flagProtocoloEntrega)},

    {caminho: 'dataInicialAgendamento', aba: 'agendamento', mensagem: OBRIGATORIA, invalido: v => vazio(v.dataInicialAgendamento)},
    {caminho: 'dataInicialAgendamento', aba: 'agendamento', mensagem: DATA_INVALIDA, invalido: v => dataInvalida(v.dataInicialAgendamento, FORMATO_DATA_HORA_WIRE)},
    {caminho: 'dataFinalAgendamento', aba: 'agendamento', mensagem: OBRIGATORIA, invalido: v => vazio(v.dataFinalAgendamento)},
    {caminho: 'dataFinalAgendamento', aba: 'agendamento', mensagem: DATA_INVALIDA, invalido: v => dataInvalida(v.dataFinalAgendamento, FORMATO_DATA_HORA_WIRE)},
    {
        caminho: 'dataFinalAgendamento', aba: 'agendamento', mensagem: DATA_FINAL_ANTES_DO_INICIO,
        invalido: v => !dataAteh(v.dataInicialAgendamento, v.dataFinalAgendamento, FORMATO_DATA_HORA_WIRE)
    },

    {caminho: 'email.idCampus', aba: 'email', mensagem: OBRIGATORIO, invalido: v => vazio(v.email?.idCampus)},
    {caminho: 'email.email', aba: 'email', mensagem: OBRIGATORIO, invalido: v => vazio(v.email?.email)},
    {caminho: 'email.email', aba: 'email', mensagem: 'E-mail inválido', invalido: v => !vazio(v.email?.email) && !validateEmail(String(v.email.email).trim())},

    {caminho: 'flagInscricaoCargaPropria', aba: 'carga', mensagem: OBRIGATORIO, invalido: v => vazio(v.flagInscricaoCargaPropria)},
    // Mesma regra do ProcessoSeletivoBean.validar: carga própria exige ao menos um candidato.
    {
        caminho: 'flagInscricaoCargaPropria', aba: 'carga', mensagem: 'Para aceitar inscrição somente da carga, informe ao menos um candidato',
        invalido: v => String(v.flagInscricaoCargaPropria) === 'true' && quantidade(v.cargas) === 0
    }
]);

/* Erros do formulário como [{caminho, mensagem}], na ordem das regras e só o primeiro de cada campo. */
export const validarProcessoSelecao = valores => {
    const erros = [];
    const vistos = new Set();
    REGRAS.forEach(regra => {
        if (!vistos.has(regra.caminho) && regra.invalido(valores || {})) {
            vistos.add(regra.caminho);
            erros.push({caminho: regra.caminho, mensagem: regra.mensagem});
        }
    });
    return erros;
};

/* Schema Yup (para o yupResolver): uma validação por regra. */
export const criarSchemaProcessoSelecao = () => REGRAS.reduce((schema, regra, indice) => schema.test(
    `regra-${indice}`,
    regra.mensagem,
    function validar(valores) {
        return regra.invalido(valores || {}) ? this.createError({path: regra.caminho, message: regra.mensagem}) : true;
    }
), Yup.object());

const ABA_POR_CAMINHO = REGRAS.reduce((mapa, regra) => ({...mapa, [regra.caminho]: regra.aba}), {});

/* Ids das abas com erro. */
export const abasComErro = erros => new Set(caminhosComErro(erros).map(caminho => ABA_POR_CAMINHO[caminho]).filter(Boolean));

/* Índice (em ABAS) da primeira aba com erro, ou -1. */
export const primeiraAbaComErro = erros => {
    const abas = abasComErro(erros);
    return ABAS.findIndex(aba => abas.has(aba.id));
};
